import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [salesRes, productsRes, damagedRes, investorsRes] =
          await Promise.all([
            axios.get("https://vape-backend-1.onrender.com/api/sales"),
            axios.get("https://vape-backend-1.onrender.com/api/products"),
            axios.get("https://vape-backend-1.onrender.com/api/damaged"),
            axios.get("https://vape-backend-1.onrender.com/api/investors"),
          ]);
        const products = productsRes.data;
        const totalStock = products.reduce((s, p) => s + p.totalStock, 0);
        const totalInvested = investorsRes.data.reduce(
          (s, i) => s + i.totalInvested,
          0,
        );
        const salesByProduct = {};
        salesRes.data.sales.forEach((s) => {
          if (!salesByProduct[s.productName]) salesByProduct[s.productName] = 0;
          salesByProduct[s.productName] += s.profit;
        });
        const chartData = Object.entries(salesByProduct).map(
          ([name, profit]) => ({ name, profit }),
        );
        setStats({
          ...salesRes.data.summary,
          totalStock,
          totalInvested,
          totalLoss: damagedRes.data.totalLoss,
          productCount: products.length,
          recentSales: salesRes.data.sales.slice(0, 5),
          chartData,
        });
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    fetchStats();
  }, []);

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Overview of your vape business</p>
        </div>
      </div>

      <div className="stat-grid">
        <div className="stat-card cyan">
          <div className="stat-label">Total Revenue</div>
          <div className="stat-value">
            ৳{(stats?.totalRevenue || 0).toLocaleString()}
          </div>
          <div className="stat-sub">সব sales এর মোট আয়</div>
        </div>
        <div className="stat-card green">
          <div className="stat-label">Total Profit</div>
          <div className="stat-value">
            ৳{(stats?.totalProfit || 0).toLocaleString()}
          </div>
          <div className="stat-sub">Revenue − buy cost</div>
        </div>
        <div className="stat-card yellow">
          <div className="stat-label">Total Invested</div>
          <div className="stat-value">
            ৳{(stats?.totalInvested || 0).toLocaleString()}
          </div>
          <div className="stat-sub">সব investor মিলিয়ে</div>
        </div>
        <div className="stat-card red">
          <div className="stat-label">Total Loss</div>
          <div className="stat-value">
            ৳{(stats?.totalLoss || 0).toLocaleString()}
          </div>
          <div className="stat-sub">Damaged goods থেকে</div>
        </div>
        <div className="stat-card cyan">
          <div className="stat-label">Total Stock</div>
          <div className="stat-value">{stats?.totalStock || 0}</div>
          <div className="stat-sub">Units বাকি আছে</div>
        </div>
        <div className="stat-card green">
          <div className="stat-label">Units Sold</div>
          <div className="stat-value">{stats?.totalQty || 0}</div>
          <div className="stat-sub">মোট বিক্রি হয়েছে</div>
        </div>
      </div>

      {/* Responsive 2-col on desktop, 1-col on mobile */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 16,
        }}
      >
        {stats?.chartData?.length > 0 && (
          <div className="card">
            <div className="card-header">
              <span className="card-title">Profit by Product</span>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={stats.chartData}
                margin={{ left: 0, right: 8, top: 4, bottom: 0 }}
              >
                <XAxis
                  dataKey="name"
                  tick={{ fill: "#8892a4", fontSize: 10 }}
                />
                <YAxis tick={{ fill: "#8892a4", fontSize: 10 }} width={50} />
                <Tooltip
                  contentStyle={{
                    background: "#151b28",
                    border: "1px solid #1e2940",
                    borderRadius: 8,
                    fontSize: "0.82rem",
                  }}
                  labelStyle={{ color: "#e8eaf0" }}
                  itemStyle={{ color: "#00e5ff" }}
                />
                <Bar dataKey="profit" radius={[4, 4, 0, 0]}>
                  {stats.chartData.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={entry.profit >= 0 ? "#00f5a0" : "#ff4d6d"}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="card">
          <div className="card-header">
            <span className="card-title">Recent Sales</span>
          </div>
          {stats?.recentSales?.length === 0 ? (
            <div className="empty-state">
              <div className="icon">💰</div>
              <p>No sales yet</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Color</th>
                    <th>Qty</th>
                    <th>Profit</th>
                  </tr>
                </thead>
                <tbody>
                  {stats?.recentSales?.map((sale) => (
                    <tr key={sale._id}>
                      <td style={{ fontSize: "0.82rem" }}>
                        {sale.productName}
                      </td>
                      <td>
                        <span
                          className="badge-color"
                          style={{ background: "var(--bg-hover)" }}
                        >
                          {sale.color}
                        </span>
                      </td>
                      <td>{sale.quantity}</td>
                      <td
                        className={
                          sale.profit >= 0 ? "text-profit" : "text-loss"
                        }
                        style={{ fontWeight: 600 }}
                      >
                        ৳{sale.profit.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
